import { useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useSelector } from "react-redux";
import type { RootState } from "@/app/rootReducer";
import { useIsImpersonating } from "./useIsImpersonating";

/**
 * Tags Microsoft Clarity sessions with audience segment data so recordings
 * and heatmaps can be filtered by role and internal/external traffic.
 *
 * Deliberately PII-free: the identify call uses the employee UUID (never
 * email/name), and tags carry only the business-role internal name and
 * boolean-ish flags.
 */
export function useClarityIdentity(): void {
  const { isAuthenticated, isLoading } = useAuth0();
  const isImpersonating = useIsImpersonating();
  const userAppSettings = useSelector(
    (state: RootState) => state.userAppSettings.userAppSettings,
  );

  const employeeUuid = userAppSettings?.employeeUuid;
  const roleName = userAppSettings?.roleName;
  const isCertainPathCompany = userAppSettings?.isCertainPathCompany;

  useEffect(() => {
    if (isLoading || !window.clarity) {
      return;
    }

    if (!isAuthenticated || !employeeUuid) {
      window.clarity("set", "userType", "anonymous");
      return;
    }

    window.clarity("identify", employeeUuid);
    window.clarity(
      "set",
      "userType",
      isCertainPathCompany ? "internal" : "external",
    );
    if (roleName) {
      window.clarity("set", "userRole", roleName);
    }
    window.clarity(
      "set",
      "isImpersonating",
      isImpersonating ? "true" : "false",
    );
  }, [
    isLoading,
    isAuthenticated,
    employeeUuid,
    roleName,
    isCertainPathCompany,
    isImpersonating,
  ]);
}
